// @flow
import React from "react";
import { connect } from "react-redux";
import { getBackend as getBackend2 } from "../../../lib/backend/Backend2";
import * as t from "../../../lib/backend/manufacturing2.generated.types";
import * as t2 from "../../../lib/SimulationReportModels.tsx";
import type { IGetPlanTableExportRes } from "../../../lib/backend/reports.generated.types";
import { translate } from "../../../components/IntlProviderWrapper";
import {
    niceDateTime,
    createPlainTextAndDownload,
    dateFromShiftTime,
    TIME_RANGES,
    niceNumber,
} from "../../../lib/Util";
import * as Auth from "../../../lib/Auth";
import * as Bl from "../../../lib/BusinessLogic";
import { PLANT_TAGS_ACCESS } from "../../../lib/ManufacturingTags.generated";
import { SET_ERROR } from "./reducers/planningTable.tsx";
import { PARALLEL_REPORT_LOADING_ACTION_NAME, ADD_REPORT_TO_DELETE } from "./reducers/parallelReport.tsx";
import { runLongParallelQuickSimForExport } from "./lib/exportBaselineParallelReport.ts";
import { type ReportParameters } from "./reducers/common.tsx";
import type { SetErrorAction } from "./reducers/planningTable.tsx";
import type { LineOrders } from "./reducers/linesOrders.tsx";
import type { ReduxState } from "./reducers/reducers.tsx";
import type {
    ParallelReportLoading,
    AddReportToDelete,
    ReportsToDelete,
    UpdateParallelReportParameters,
} from "./reducers/parallelReport.tsx";
import type { SetSkipStockReport } from "./reducers/properties.tsx";
import { ORDER_TAGS_ACCESS } from "../../../lib/ManufacturingTags.generated";

type Props = {
    parallel_original_report_uuid: string | null,
    lines_orders: LineOrders[],
    unscheduled_orders: LineOrders[],
    reduxDispatch: (
        args:
            | SetErrorAction
            | ParallelReportLoading
            | AddReportToDelete
            | SetSkipStockReport
            | UpdateParallelReportParameters
    ) => void,
    parameters: ReportParameters | null,
    reports_to_delete: ReportsToDelete,
    report_data: t2.IReportModelEx | null,
};

type State = {
    loading: boolean,
};

type OrdersToolsMap = {
    [key: string]: t.IOrderTool,
};

class SaveToTxtButton extends React.Component<Props, State> {
    state: State = {
        loading: false,
    };

    getTools = async (order_external_ids: string[]): Promise<OrdersToolsMap> => {
        const res = await getBackend2().manufacturing.getOrdersTools({ order_external_ids });

        const orders_tools_map = {};
        for (const ot of res.orders_tools) {
            orders_tools_map[ot.order_external_id] = ot;
        }
        return orders_tools_map;
    };

    onSimpleExport = async () => {
        const report_data = this.props.report_data;
        if (!report_data) {
            return;
        }

        // check flag on plant for skipping tool setup time in export
        let skip_tool_setup_time = true;
        const plant_uuid = report_data.result.orders[0].plant;
        const plants = Bl.getPlantsForUser();
        const plant = plants.find(p => p.uuid === plant_uuid);
        if (plant !== undefined) {
            skip_tool_setup_time = PLANT_TAGS_ACCESS.export_operation_start_at_setup_end(plant.tags ?? {});
        }

        const rows: string[][] = [];

        const h = TIME_RANGES.HOUR;
        const from_shift_time_ts = dateFromShiftTime(report_data.result.from_shift_time).getTime();

        const orders_map: Map<string, t2.ISimulationReportOrderEx> = new Map();
        report_data.result.orders.forEach(line => {
            line.production.forEach(op => {
                orders_map.set(op.order_id, op);
            });
        });

        rows.push([
            "line",
            "material",
            "order",
            "process_num",
            "quantity",
            "start",
            "end",
            "deadline",
            "delay_h",
            "priority",
            "tool_setup",
            "type",
            "title",
        ]);
        for (const line of this.props.lines_orders) {
            for (const op of line.orders) {
                const other_data = orders_map.get(op.uuid);
                if (other_data === undefined) {
                    continue; // some weird internal state
                }
                const cleaning_type = other_data.immutable_meta_data?.cleaning_type ?? "-";
                let start_ts = from_shift_time_ts + other_data.estimated_start[1].val * h;
                const end_ts = from_shift_time_ts + other_data.estimated_completion[1].val * h;
                // tool-setup duration in hours
                const tool_setup = other_data.estimated_tool_setup_end[1].val - other_data.estimated_start[1].val;
                if (skip_tool_setup_time) {
                    start_ts += tool_setup * h;
                }

                let priority = ORDER_TAGS_ACCESS.priority(op.tags); // "" if missing
                let deadline = ORDER_TAGS_ACCESS.required_deadline(op.tags); // 0 if missing
                let deadline_str = "-";
                let delay = "-";
                if (deadline !== 0) {
                    const delay_num = -(deadline - end_ts) / h;
                    delay = niceNumber(delay_num, 2); // delay in hours
                    deadline_str = niceDateTime(new Date(deadline));
                }
                rows.push([
                    line.line_external_id,
                    op.material_external_id,
                    op.external_id,
                    op.process_num,
                    niceNumber(op.quantity_total),
                    niceDateTime(new Date(start_ts)),
                    niceDateTime(new Date(end_ts)),
                    deadline_str,
                    delay,
                    priority,
                    niceNumber(tool_setup, 2),
                    cleaning_type,
                    op.material_title,
                ]);
            }
        }

        // Process unscheduled orders
        for (const line of this.props.unscheduled_orders) {
            for (const op of line.orders) {
                let priority = ORDER_TAGS_ACCESS.priority(op.tags); // "" if missing
                let deadline = ORDER_TAGS_ACCESS.required_deadline(op.tags); // 0 if missing
                let deadline_str = "-";
                if (deadline !== 0) {
                    deadline_str = niceDateTime(new Date(deadline));
                }

                rows.push([
                    line.line_external_id,
                    op.material_external_id,
                    op.external_id,
                    op.process_num,
                    niceNumber(op.quantity_total),
                    "-", // start
                    "-", // end
                    deadline_str,
                    "-", // delay_h
                    priority,
                    "-", // tool_setup
                    "-", // type (cleaning_type)
                    op.material_title,
                ]);
            }
        }

        const txt_file = rows.map(r => r.join("\t")).join("\n");
        const filename = `planning_table_export_${new Date().getTime()}.txt`;
        createPlainTextAndDownload(txt_file, filename);
    };

    onExport = async () => {
        const is_agrana = Auth.getOrgData().code == "agrana";
        const is_demo = Auth.getOrgData().code.startsWith("qindustry");
        if (is_agrana || is_demo) {
            this.onSimpleExport();
            return;
        }

        const reportParameters = this.props.parameters;
        const initialReportUuid = this.props.parallel_original_report_uuid;
        if (reportParameters === null || initialReportUuid === null) {
            return;
        }

        const line_uuids = this.props.lines_orders.map(l => l.line_uuid);

        this.props.reduxDispatch({ type: PARALLEL_REPORT_LOADING_ACTION_NAME, data: true });
        let result: IGetPlanTableExportRes | null = null;

        try {
            const outcome = await runLongParallelQuickSimForExport(
                this.props.reduxDispatch,
                reportParameters,
                initialReportUuid
            );
            const current_report_uuid = outcome?.result.report_uuid;
            if (!current_report_uuid) return;

            result = await getBackend2().reports.getPlanTableExport({
                initial_report_uuid: initialReportUuid,
                final_report_uuid: current_report_uuid,
                line_uuids,
            });

            this.props.reduxDispatch({ type: ADD_REPORT_TO_DELETE, data: current_report_uuid });
        } catch (e) {
            this.props.reduxDispatch({ type: SET_ERROR, data: e.message });
            return;
        } finally {
            this.props.reduxDispatch({ type: PARALLEL_REPORT_LOADING_ACTION_NAME, data: false });
        }

        let txt_file = "";
        if (result && result.report_export && result.report_export.order_changes) {
            txt_file +=
                "order_external_id  process_num  line_external_id  start_time            end_time              tool_external_id\n";
            txt_file +=
                "=================  ===========  ================  ====================  ====================  ================\n";

            const order_external_ids = result.report_export.order_changes.map(oc => oc.order_external_id);
            const orders_tools = await this.getTools(order_external_ids);

            const padExport = (value: string, width: number): string => {
                return String(value ?? "")
                    .padEnd(width)
                    .slice(0, width);
            };

            const widths = {
                order_external_id: 17,
                process_num: 11,
                line_external_id: 16,
                start_time: 20,
                end_time: 20,
                tool_external_id: 16,
            };

            const missing_tools = [];
            for (const order of result.report_export.order_changes) {
                for (const change of order.op_changes) {
                    const tool = orders_tools[order.order_external_id];
                    let tool_external_id = "-";
                    if (tool) {
                        tool_external_id = tool.tool_external_id;
                    } else {
                        missing_tools.push(order.order_external_id);
                    }

                    const start_time = niceDateTime(new Date(change.final_epoch_start ?? 0));
                    const end_time = niceDateTime(new Date(change.final_epoch_end ?? 0));
                    const line_external_id = change.final_line_external_id || change.initial_line_external_id || "N/A";

                    const line =
                        padExport(order.order_external_id, widths.order_external_id) +
                        "  " +
                        padExport(change.process_num, widths.process_num) +
                        "  " +
                        padExport(line_external_id, widths.line_external_id) +
                        "  " +
                        padExport(start_time, widths.start_time) +
                        "  " +
                        padExport(end_time, widths.end_time) +
                        "  " +
                        padExport(tool_external_id, widths.tool_external_id);

                    txt_file += line + "\n";
                }
            }
            if (missing_tools.length > 0) {
                const error_msg =
                    translate("common.error_tool", "Some orders do not have tools.") + ": " + missing_tools.join(",");
                this.props.reduxDispatch({ type: SET_ERROR, data: error_msg });
            }
        }

        const filename = `planning_table_export_${new Date().getTime()}.txt`;
        return createPlainTextAndDownload(txt_file, filename);
    };

    isDisabled = () => {
        return !this.props.parallel_original_report_uuid;
    };

    render() {
        const is_disabled = this.isDisabled();
        return (
            <button className="dropdown-item" onClick={this.onExport} disabled={is_disabled}>
                {is_disabled && <i className="fas fa-spinner fa-spin" />}{" "}
                {translate("common.export_in_text_format", "Export in text format")}
            </button>
        );
    }
}

export default connect(
    (state: ReduxState) => {
        const parallel_report = state.gantt_chart_parallel_report;
        const report_data = state.gantt_chart_report.report_data;

        return {
            parallel_original_report_uuid: parallel_report.parallel_original_report_uuid,
            lines_orders: state.gantt_chart_lines_orders.lines_orders,
            unscheduled_orders: state.gantt_chart_lines_orders.unscheduled_orders,
            parameters: parallel_report.parameters,
            reports_to_delete: parallel_report.reports_to_delete,
            report_data,
        };
    },
    dispatch => ({ reduxDispatch: dispatch })
)(SaveToTxtButton);
